import React, { lazy, Suspense } from "react";
import { PageLoader } from "./Components";
import history from "./Utils/history";
import { Route, Router, Switch } from "react-router-dom";
import * as ROUTES from "./Constants/routesConstant";
import WebViewRouter from "./routesWebview";
import withLazyComponent from "./Utils/DashyHoc";
import useActiveSubscription from "./Hooks/useActiveSubscription";
import { SETTING_PAGE_SUBROUTES } from "./Constants/setting";
import PageLayout from "@/Components/PageLayout";

/** Components */
const ContactUs = lazy(() => import("./Containers/ContactUs"));
const EmailVerify = lazy(() => import("./Containers/EmailVerify"));
const BrandLeads = lazy(() => import("./Containers/BrandLeads"));
const ThemeExplore = lazy(() => import("./Containers/ThemeExplore"));
const Orders = lazy(() => import("./Containers/Orders"));
const OrdersV2 = lazy(() => import("./Containers/OrdersV2"));
const OrderDetail = lazy(() => import("./Containers/OrderDetail"));
const Catalog = lazy(() => import("./Containers/Catalog"));
const Settings = lazy(() => import("./Containers/Settings"));
// const EditBussiness = lazy(() => import("./Containers/EditBusiness"));
const AppSettingsComponent = lazy(() => import("./Containers/AppSettings"));
const Marketing = lazy(() => import("./Containers/Marketing"));
const CollectionsList = lazy(() => import("./Containers/CollectionsList"));
const CreateCollection = lazy(() => import("./Containers/CreateAndEditCollectionComponents"));
const EditCollection = lazy(() => import("./Containers/CreateAndEditCollectionComponents"));
const Onboarding = lazy(() => import("./Containers/Onboarding"));
const ThemePage = lazy(() => import("./Containers/ThemePage"));
const ThemeList = lazy(() => import("./Containers/ThemeList"));
const ThemePreview = lazy(() => import("./Containers/ThemePreview"));
const Payment = lazy(() => import("./Containers/Payment"));
const ThemeLoader = lazy(() => import("./Containers/ThemeLoader"));
const ThemeHistory = lazy(() => import("./Containers/ThemeHistory"));
const ThemeEdit = lazy(() => import("./Containers/ThemeEdit"));
const ThemeImageCrop = lazy(() => import("./Containers/ThemeImageCrop"));
const DomainExplore = lazy(() => import("./Containers/DomainExplore"));
const DomainSearch = lazy(() => import("./Containers/DomainSearch"));
const DomainPurchaseSuccess = lazy(
  () => import("./Containers/DomainSalesSearch/components/DomainPurchaseSuccess/DomainPurchaseSuccess"),
);
const DomainSuccess = lazy(() => import("./Containers/DomainSuccess"));
const DomainHistory = lazy(() => import("./Containers/DomainHistory"));
const DomainDetails = lazy(() => import("./Containers/DomainDetails"));
const NotFound = lazy(() => import("./Containers/404"));
const PaymentProcessing = lazy(() => import("./Containers/PaymentProcessing"));
const DomainConnectSuccess = lazy(() => import("./Containers/DomainConnectSuccess"));
const PaymentOptions = lazy(() => import("./Components/PaymentOptions"));
const StoreControls = lazy(() => import("./Containers/StoreControls"));
const CreateBill = lazy(() => import("./Containers/CreateBill"));
const MyQrCode = lazy(() => import("./Containers/MyQrCode"));
const MyPayments = lazy(() => import("./Containers/MyPayments"));
const GoogleAdsDiscover = lazy(() => import("./Containers/GoogleAdsDiscover"));
const GoogleAdManager = lazy(() => import("./Containers/GoogleAdManager"));
const GoogleAdDetails = lazy(() => import("./Containers/GoogleAdDetails"));
const GoogleAdsHistory = lazy(() => import("./Containers/GoogleAdsHistory"));
const CouponsList = lazy(() => import("./Containers/CouponsList"));
const CouponsVouchers = lazy(() => import("./Containers/CouponsVouchers"));
const CreateCoupon = lazy(() => import("./Containers/CreateCoupon"));
const PaymentLink = lazy(() => import("./Components/PaymentLinkFlow"));
const MarketingAnalytics = lazy(() => import("./Containers/MarketingTags"));
const Subscription = lazy(() => import("./Containers/Subscription"));
const SubmitSuggestion = lazy(() => import("./Containers/SubmitSuggestion"));
const MyInvoices = lazy(() => import("./Containers/MyInvoices"));
const BuySubscription = lazy(() => import("./Containers/SubscriptionLanding"));
// const PremiumSubscription = lazy(() => import("./Components/SubscriptionLayout/PremiumSubscription"));
const SubscriptionDetails = lazy(() => import("./Containers/SubscriptionDetails"));
const DeliveryPartnerPage = lazy(() => import("./Containers/DeliveryPartnerPage"));
const DeliveryPartner = lazy(() => import("./Containers/DeliveryPartner"));
const ReturnRefundPolicyPage = lazy(() => import("./Containers/ReturnRefundPolicyPage"));
const InvoiceDisclaimerPolicyPage = lazy(() => import("./Containers/InvoiceDisclaimerPolicyPage"));
const kycPage = lazy(() => import("./Containers/KycCompletionPage"));
const EditPageComponent = lazy(() => import("./Containers/EditPageComponent"));
const AdditionalPages = lazy(() => import("./Containers/AdditionalPages"));
const Carts = lazy(() => import("./Containers/Carts"));
const UnstructuredEditor = lazy(() => import("./Containers/UnstructuredEditor"));
const StaffManagement = lazy(() => import("./Containers/Staffmanagement"));
const GoogleWorkspacePrePurchase = lazy(() => import("./Containers/GoogleWorkspace/PrePurchase"));
const GoogleWorkspacePostPurchase = lazy(() => import("./Containers/GoogleWorkspace/PostPurchase"));
const WorkspaceAccountSetup = lazy(() => import("./Containers/GoogleWorkspace/WorkspaceAccountSetup/index"));
const WorkspaceAccountSetupAfterInitialSetup = lazy(
  () => import("./Containers/GoogleWorkspace/WorkspaceAccountSetupAfterInitialSetup/index"),
);
const CRM = lazy(() => import("./Containers/CustomerRelationManagement"));
const ProductCartDetails = lazy(() => import("./Containers/Carts/ProductCartDetails"));
const FacebookBusinessSuite = lazy(() => import("./Containers/FacebookBusinessSuite"));
const MyCreditsComponent = lazy(() => import("./Containers/MyCredits"));
const LeadGeneration = lazy(() => import("./Containers/LeadGeneration"));
const StorePolicy = lazy(() => import("./Containers/StorePolicy"));
const StorePolicyEditor = lazy(() => import("./Containers/StorePolicy/PolicyEditor"));
const StoreAnalytics = lazy(() => import("./Containers/Analytics/StoreAnalytics"));
const GoogleShoppingComponent = lazy(() => import("./Containers/GoogleShopping"));
const FacebookAds = lazy(() => import("./Containers/FacebookAds"));
const SEOTools = lazy(() => import("./Containers/SEOTools"));
const SmsMarketing = lazy(() => import("./Containers/SmsMarketing"));
const CreateCampaign = lazy(() => import("./Containers/CreateCampaign"));
const MobileOnboarding = lazy(() => import("./Containers/MobileOnboarding"));
const CampaignPaymentSuccess = lazy(() => import("./Components/SmsCampaign/campaignPaymentSuccess"));
const CollectionCategoryList = lazy(() => import("./Containers/CollectionCategoryList"));
const CustomDeliverySetup = lazy(() => import("./Containers/CustomDeliverySetup"));
const CustomDeliveryDetails = lazy(() => import("./Containers/CustomDeliveryDetails"));
const CustomZoneDetails = lazy(() => import("./Containers/CustomZoneSetup"));
const GSTBilling = lazy(() => import("./Containers/GSTBilling"));
const AdvCatalog = lazy(() => import("./Containers/AdvCatalog"));
const CustomerReviewRatings = lazy(() => import("./Containers/CustomerReviewRatings"));
const ManageDNSSettings = lazy(() => import("./Containers/ManageDNS"));
const SocialMediaProfiles = lazy(() => import("./Containers/SocialMediaProfiles"));
const WhatsappMarketingLanding = lazy(() =>
  import("./Containers/WhatsappMarketingLanding").then((module) => ({ default: module.WhatsappMarketingLanding })),
);
const OrderCancellationRefund = lazy(() => import("./Containers/OrderCancellationRefund"));
const WidgetPageBuilder = lazy(() => import("./Containers/WidgetPageBuilder"));
const B2B = lazy(() => import("./Containers/B2B").then((module) => ({ default: module.B2B })));
const SubscriptionPurchaseUsingCoupon = lazy(() => import("./Containers/SubscriptionPurchaseUsingCoupon"));
const OutOfStockQuery = lazy(() => import("./Containers/NotifyProduct"));
const GshopV2LandingPage = withLazyComponent(
  () => import("./Containers/GoogleShoppingV2/Containers/GshopLandingPage/GshopLandingPage"),
);
const GshopMerchantForm = withLazyComponent(
  () => import("./Containers/GoogleShoppingV2/Containers/GshopFormPage/GshopFormPage"),
);
const GshopSuccessPageV2 = withLazyComponent(
  () => import("./Containers/GoogleShoppingV2/Containers/GshopSuccessSetup/GshopSuccessSetup"),
);
const GshopDashboard = withLazyComponent(
  () => import("./Containers/GoogleShoppingV2/Containers/GshopDashboard/GshopDashboard"),
);
const BulkEdit = withLazyComponent(() => import("./Containers/BulkEdit/Containers/BulkEdit/BulkEdit"));
const DomainSalesLanding = lazy(() => import("./Containers/DomainSalesLanding/index"));
const DomainSalesCompletion = lazy(() => import("./Containers/DomainSalesCompletion/index"));
const DomainSalesSearch = lazy(() => import("./Containers/DomainSalesSearch/index"));
const CustomerAddressFields = lazy(() => import("./Containers/CustomerAddressFields/index"));
const BrandWebsiteCategories = lazy(() => import("./Containers/BrandWebsiteCategories"));
const BrandWebsiteCategoryThemes = lazy(() => import("./Containers/BrandWebsiteCategoryThemes"));
const BrandWebsiteThemePreview = lazy(() => import("./Containers/BrandWebsiteThemePreview"));
const BrandWebsiteDemo = lazy(() => import("./Containers/BrandWebsiteDemo"));
const BrandWebsitePaymentSuccess = lazy(() => import("./Containers/BrandWebsitePaymentSuccessful"));
const BrandWebsiteRenewal = lazy(() => import("./Containers/BrandWebsiteRenewalPage"));
const DomainCartPage = lazy(() => import("./Containers/DomainSalesSearch/components/CartPage"));
const PremiumPage = lazy(() => import("./Containers/NewPremiumPage/Containers/PremiumHomePage/PremiumHomePage"));
const UtilitiesPage = lazy(() => import("./Containers/AppUtilities/dashyUtilities"));
const SubscriptionStatusDetails = lazy(
  () => import("./Containers/NewPremiumPage/Containers/SubscriptionStatusDetails/SubscriptionStatusDetails"),
);
const DomainLandingPage = lazy(() => import("./Containers/DomainLandingPage"));
const ExitGatingHomePage = lazy(() => import("./Containers/ExitGatingHomePage"));
const ExitGatingEditorPage = lazy(() => import("./Containers/ExitGatingEditorPage"));
const GenerateShippingLabel = lazy(() => import("./Containers/GenerateShippingLabel"));
const TitanDashboard = lazy(() => import("./Containers/TitanEmailDashboard"));
const RedirectionConnector = lazy(() => import("./Containers/RedirectionConnector"));
const BlogEditor = lazy(() => import("./Containers/Blogs/Editor"));
const BlogsDashboard = lazy(() => import("./Containers/Blogs/Dashboard"));
const Wishlists = lazy(() => import("./Containers/Wishlists"));
const SeoDashboard = lazy(() => import("./Containers/SEO/pages/Dashboard"));
const SeoRedirectManager = lazy(() => import("./Containers/SEO/pages/RedirectManager"));
const SeoPageReport = lazy(() => import("./Containers/SEO/pages/PageReport"));
const ShowroomPages = lazy(() => import("./Containers/ShowroomPages"));

const AppRouter = (): JSX.Element => {
  const { isBrandWebsiteThemePurchased } = useActiveSubscription();

  return (
    <Router history={history}>
      <PageLayout>
        <Suspense fallback={<PageLoader show />}>
          <Switch>
            <Route path={ROUTES.WEBVIEW_PREFIX_ROUTE}>
              <WebViewRouter />
            </Route>
            <Route exact path={ROUTES.MOBILE_ONBOARDING} component={MobileOnboarding} />
            <Route exact path={ROUTES.EMAIL_VERIFICATION_ROUTE} component={EmailVerify} />
            <Route exact path={ROUTES.ORDERS_ROUTE} component={OrdersV2} />
            <Route exact path={ROUTES.ORDERS_V2_ROUTE} component={Orders} />
            <Route exact path={ROUTES.LEADS_ROUTE} component={isBrandWebsiteThemePurchased ? BrandLeads : Carts} />
            <Route exact path={ROUTES.CART_PRODUCT_DETAILS_ROUTE} component={ProductCartDetails} />
            <Route exact path={ROUTES.ORDER_DETAIL_ROUTE} component={OrderDetail} />
            <Route exact path={ROUTES.CATALOG_ROUTE} component={Catalog} />
            <Route path={ROUTES.SETTINGS_ROUTE} component={Settings} />
            <Route exact path={ROUTES.SOCIAL_MEDIA_PROFILE_ROUTE} component={SocialMediaProfiles} />
            <Route exact path={ROUTES.SETTINGS_APP_SETTINGS_ROUTE} component={AppSettingsComponent} />
            <Route exact path={ROUTES.MARKETING_ROUTE} component={Marketing} />
            <Route exact path={ROUTES.LOGIN_PAGE} component={Onboarding} />
            <Route exact path={ROUTES.ONBOARDING_ROUTE} component={Onboarding} />
            <Route exact path={ROUTES.THEME_ROUTE} component={ThemePage} />
            <Route exact path={ROUTES.THEMES_EXPLORE_ROUTE} component={ThemeExplore} />
            <Route exact path={ROUTES.THEMES_CATEGORY_ROUTE} component={ThemeList} />
            <Route exact path={ROUTES.THEMES_COLOR_ROUTE} component={ThemeList} />
            <Route exact path={ROUTES.THEMES_PREVIEW_ROUTE} component={ThemePreview} />
            <Route exact path={ROUTES.PAYMENT_ROUTE} component={Payment} />
            <Route exact path={ROUTES.THEMES_LOADER_ROUTE} component={ThemeLoader} />
            <Route exact path={ROUTES.THEMES_HISTORY_ROUTE} component={ThemeHistory} />
            <Route exact path={ROUTES.WEB_EDITOR_ROUTE} component={ThemeEdit} />
            <Route exact path={ROUTES.THEMES_IMAGE_CROP_ROUTE} component={ThemeImageCrop} />
            <Route exact path={ROUTES.PAYMENT_PROCESSING_ROUTE} component={PaymentProcessing} />
            <Route exact path={ROUTES.DOMAIN_EXPLORE_ROUTE} component={DomainExplore} />
            <Route exact path={ROUTES.DOMAIN_SEARCH_ROUTE} component={DomainSearch} />
            <Route exact path={ROUTES.DOMAIN_SUCCESS_ROUTE} component={DomainSuccess} />
            <Route exact path={ROUTES.DOMAIN_HISTORY_ROUTE} component={DomainHistory} />
            <Route exact path={ROUTES.DOMAIN_DETAILS_ROUTE} component={DomainDetails} />
            <Route exact path={ROUTES.DOMAIN_CONNECT_SUCCESS_ROUTE} component={DomainConnectSuccess} />
            <Route exact path={ROUTES.PAYMENT_OPTIONS_ROUTES} component={PaymentOptions} />
            <Route exact path={ROUTES.CREATE_BILL_ROUTE} component={CreateBill} />
            <Route exact path={ROUTES.PAYMENT_LINK_ROUTE} component={PaymentLink} />
            <Route exact path={ROUTES.MARKETING_MY_QR_CODE_ROUTE} component={MyQrCode} />
            <Route exact path={ROUTES.MY_PAYMENTS_ROUTE} component={MyPayments} />
            <Route exact path={ROUTES.MARKETING_ADS_DISCOVER_ROUTE} component={GoogleAdsDiscover} />
            <Route exact path={ROUTES.MARKETING_AD_STEP_ROUTE} component={GoogleAdManager} />
            <Route exact path={ROUTES.MARKETING_AD_DETAILS_ROUTE} component={GoogleAdDetails} />
            <Route exact path={ROUTES.MARKETING_ADS_HISTORY_ROUTE} component={GoogleAdsHistory} />
            <Route exact path={ROUTES.MARKETING_COUPONS_LIST} component={CouponsVouchers} />
            <Route exact path={ROUTES.CREATE_COUPON_COMPLETE_ROUTE} component={CreateCoupon} />
            <Route exact path={ROUTES.COLLECTIONS_LIST} component={CollectionsList} />
            <Route exact path={ROUTES.ADD_COLLECTION} component={CreateCollection} />
            <Route exact path={ROUTES.EDIT_COLLECTION} component={EditCollection} />
            <Route exact path={ROUTES.COLLECTION_CATEGORY_LIST} component={CollectionCategoryList} />
            <Route exact path={ROUTES.GST_BILLING_ROUTE} component={GSTBilling} />
            <Route exact path={ROUTES.SUBMIT_SUGGESTION_ROUTE} component={SubmitSuggestion} />
            <Route exact path={ROUTES.ORDER_CANCELLATION_REFUND_ROUTE} component={OrderCancellationRefund} />
            <Route exact path={ROUTES.CUSTOMER_REVIEW_RATINGS_ROUTE} component={CustomerReviewRatings} />
            <Route exact path={ROUTES.MARKETING_ANALYTICS_ROUTE} component={MarketingAnalytics} />
            <Route exact path={ROUTES.DELIVERY_PARTNER_COMPLETE_ROUTE} component={DeliveryPartnerPage} />
            <Route exact path={ROUTES.DELIVERY_PARTNER_DETAIL_ROUTE} component={DeliveryPartner} />
            <Route exact path={ROUTES.RETURN_REFUND_POLICY_ROUTE} component={ReturnRefundPolicyPage} />
            <Route exact path={ROUTES.INVOICE_DISCLAIMER_POLICY_ROUTE} component={InvoiceDisclaimerPolicyPage} />
            <Route exact path={ROUTES.SETUP_DELIVERY_ROUTE} component={kycPage} />
            {/* Subscription */}
            <Route exact path={ROUTES.SUBSCRIPTION_ROUTE} component={Subscription} />
            <Route exact path={ROUTES.SUBSCRIPTION_DETAILS_ROUTE} component={SubscriptionDetails} />
            <Route exact path={ROUTES.BUY_SUBSCRIPTION} component={BuySubscription} />
            <Route exact path={ROUTES.EDIT_PAGE_COMPLETE_ROUTE} component={EditPageComponent} />
            <Route exact path={ROUTES.ADDITIONAL_PAGES_ROUTE} component={AdditionalPages} />
            <Route exact path={ROUTES.ADDITIONAL_PAGES_EDITOR_ROUTE} component={UnstructuredEditor} />
            <Route exact path={ROUTES.STAFF_DETAILS_ROUTE} component={StaffManagement} />
            <Route exact path={ROUTES.MY_CREDITS_ROUTE} component={MyCreditsComponent} />
            <Route exact path={ROUTES.WORKSPACE_PREPURCHASE_ROUTE} component={GoogleWorkspacePrePurchase} />
            <Route exact path={ROUTES.WORKSPACE_POSTPURCHASE_ROUTE} component={GoogleWorkspacePostPurchase} />
            <Route exact path={ROUTES.WORKSPACE_ACCOUNT_SETUP} component={WorkspaceAccountSetupAfterInitialSetup} />
            <Route exact path={ROUTES.WORKSPACE_ACCOUNT_INITIAL_SETUP} component={WorkspaceAccountSetup} />
            <Route exact path={ROUTES.CRM_ROUTE} component={CRM} />
            <Route exact path={ROUTES.FACEBOOK_BUSINESS_SUITE_ROUTE} component={FacebookBusinessSuite} />
            <Route exact path={ROUTES.LEAD_GENERATION_ROUTE} component={LeadGeneration} />
            <Route exact path={SETTING_PAGE_SUBROUTES.STORE_POLICIES} component={StorePolicy} />
            <Route exact path={ROUTES.CONTACT_US} component={ContactUs} />
            <Route exact path={ROUTES.SETTINGS_STORE_POLICY + "/:id"} component={StorePolicyEditor} />
            <Route exact path={ROUTES.STORE_ANALYTICS_ROUTE} component={StoreAnalytics} />
            <Route
              exact
              path={ROUTES.MARKETING_ROUTE + ROUTES.GOOGLE_SHOPPING_ROUTE}
              component={GoogleShoppingComponent}
            />
            <Route exact path={ROUTES.MARKETING_ROUTE + ROUTES.FACEBOOK_ADS} component={FacebookAds} />
            <Route exact path={ROUTES.MARKETING_ROUTE + ROUTES.SEO_TOOLS} component={SEOTools} />
            <Route exact path={ROUTES.SMS_MARKETING_ROUTE} component={SmsMarketing} />
            <Route exact path={ROUTES.MARKETING_ROUTE + ROUTES.CREATE_CAMPAIGN_ROUTE} component={CreateCampaign} />
            <Route exact path={ROUTES.SMS_CAMPAIGN_PAYMENT_SUCCESS_ROUTE} component={CampaignPaymentSuccess} />
            <Route exact path={ROUTES.CUSTOM_DELIVERY_SETUP + "/:itemId?"} component={CustomDeliverySetup} />
            <Route exact path={ROUTES.CUSTOM_DELIVERY_DETAILS} component={CustomDeliveryDetails} />
            <Route exact path={ROUTES.CUSTOM_ZONES_SETUP + "/:itemId?"} component={CustomZoneDetails} />
            {/* <Route exact path="/kinetic-ui" component={Sample} /> */}
            <Route exact path={ROUTES.CATALOG_COMPLETE_ROUTE} component={AdvCatalog} />
            <Route exact path={ROUTES.WHATSAPP_BUSINESS_DEMO} component={WhatsappMarketingLanding} />
            <Route exact path={ROUTES.SETUP_B2B_ROUTE} component={B2B} />
            <Route exact path={ROUTES.OUT_OF_STOCK_ROUTE} component={OutOfStockQuery} />
            <Route exact path={ROUTES.GOOGLE_SHOPPING_V2_LANDING_PAGE} component={GshopV2LandingPage} />
            <Route exact path={ROUTES.GOOGLE_SHOPPING_V2_MERCHANT_FORM} component={GshopMerchantForm} />
            <Route exact path={ROUTES.GOOGLE_SHOPPING_V2_SUCCESS_PAGE} component={GshopSuccessPageV2} />
            <Route exact path={ROUTES.GOOGLE_SHOPPING_V2_DASHBOARD} component={GshopDashboard} />
            <Route exact path={ROUTES.BULK_EDIT} component={BulkEdit} />
            <Route
              exact
              path={ROUTES.SUBSCRIPTION_PAYMENT_USING_COUPONS_ROUTE}
              component={SubscriptionPurchaseUsingCoupon}
            />
            <Route exact path={ROUTES.PREMIUM_PAGE} component={PremiumPage} />
            <Route exact path={ROUTES.APP_UTILITIES} component={UtilitiesPage} />
            <Route exact path={ROUTES.SUBSCRIPTION_STATUS_DETAILS} component={SubscriptionStatusDetails} />
            <Route exact path={ROUTES.PAGE_BUILDER_ROUTE} component={WidgetPageBuilder} />
            <Route exact path={ROUTES.DOMAIN_SALE_ROUTE} component={DomainSalesLanding} />
            <Route exact path={ROUTES.DOMAIN_COMPLETION_ROUTE} component={DomainSalesCompletion} />
            <Route exact path={ROUTES.DOMAIN_OPEN_SEARCH_ROUTE} component={DomainSalesSearch} />
            <Route exact path={ROUTES.DOMAIN_PURCHASE_SUCCESS} component={DomainPurchaseSuccess} />
            <Route exact path={ROUTES.MANAGE_DNS_ROUTE} component={ManageDNSSettings} />
            <Route exact path={ROUTES.CUSTOMER_ADDRESS_FIELD} component={CustomerAddressFields} />
            <Route exact path={ROUTES.BRAND_WEBSITE_BUSINESS_CATEGORIES} component={BrandWebsiteCategories} />
            <Route exact path={ROUTES.BRAND_WEBSITE_CATEGORY_THEMES + "/:id"} component={BrandWebsiteCategoryThemes} />
            <Route exact path={ROUTES.BRAND_WEBSITE_THEME_DEMO + "/:id"} component={BrandWebsiteDemo} />
            <Route exact path={ROUTES.BRAND_WEBSITE_THEME_PREVIEW + "/:id"} component={BrandWebsiteThemePreview} />
            <Route exact path={ROUTES.BRAND_WEBSITE_PAYMENT_SUCCESS} component={BrandWebsitePaymentSuccess} />
            <Route exact path={ROUTES.BRAND_WEBSITE_RENEWAL_PAGE} component={BrandWebsiteRenewal} />
            <Route exact path={ROUTES.DOMAIN_CART_PAGE} component={DomainCartPage} />
            <Route path={ROUTES.DOMAIN_LANDING_PAGE_PREFIX} component={DomainLandingPage} />
            <Route exact path={ROUTES.EXIT_GATING_HOMEPAGE} component={ExitGatingHomePage} />
            <Route exact path={ROUTES.EXIT_GATING_EDITOR} component={ExitGatingEditorPage} />
            <Route exact path={ROUTES.MY_INVOICES_ROUTE} component={MyInvoices} />
            <Route exact path={ROUTES.SHIPPING_LABEL_GENERATOR} component={GenerateShippingLabel} />
            <Route exact path={ROUTES.TITAN_DASHBOARD} component={TitanDashboard} />
            <Route exact path={ROUTES.REDIRECTION_CONNECTOR} component={RedirectionConnector} />
            <Route exact path={ROUTES.BLOG_EDITOR} component={BlogEditor} />
            <Route exact path={ROUTES.BLOGS_DASHBOARD} component={BlogsDashboard} />
            <Route exact path={ROUTES.WISHLISTS_ROUTE} component={Wishlists} />
            <Route exact path={ROUTES.SEO_DASHBOARD} component={SeoDashboard} />
            <Route exact path={ROUTES.SEO_URL_REDIRECT_MANAGER} component={SeoRedirectManager} />
            <Route exact path={ROUTES.SEO_PAGE_REPORT} component={SeoPageReport} />
            <Route
              exact
              path={ROUTES.SHOWROOM_ABOUT_US}
              render={() => <ShowroomPages pageSlug="about-us" pageTitle="About Us" />}
            />
            <Route
              exact
              path={ROUTES.SHOWROOM_PRIVACY_POLICY}
              render={() => <ShowroomPages pageSlug="privacy-policy" pageTitle="Privacy Policy" />}
            />
            <Route
              exact
              path={ROUTES.SHOWROOM_TERMS_AND_CONDITIONS}
              render={() => <ShowroomPages pageSlug="terms-and-conditions" pageTitle="Terms & Conditions" />}
            />
            <Route component={NotFound} />
          </Switch>
        </Suspense>
      </PageLayout>
    </Router>
  );
};
export default React.memo(AppRouter);
